import {
  FreelancerStatus,
  InvoiceSource,
  InvoiceStatus,
  PaymentMethod,
  paymentType,
  recipientType,
} from '../enums';
import { countryList } from './index';

export const paymentMethodLabels: Record<PaymentMethod, string> = {
  [PaymentMethod.Bank]: 'Bank transfer',
  [PaymentMethod.Paypal]: 'Paypal',
  [PaymentMethod.Payoneer]: 'Payoneer',
};

export const paymentMethods = [
  PaymentMethod.Bank,
  PaymentMethod.Paypal,
  PaymentMethod.Payoneer,
];

// Payoneer is no longer offered when an invoice is created. It stays in the
// list above so existing Payoneer invoices keep their label, icon and filter.
export const selectablePaymentMethods = paymentMethods.filter(
  (method) => method !== PaymentMethod.Payoneer,
);

// The invoice filter dropdown is label-based; map the picked label back to the
// value the API expects.
export const getPaymentMethodByLabel = (label: string) =>
  paymentMethods.find((method) => paymentMethodLabels[method] === label);

// Members/freelancers store a wallet email rather than a payment type, so the
// type is derived: a stored wallet wins over bank details.
export const getMemberPaymentMethod = (member?: {
  paypal_email?: string;
  payoneer_email?: string;
}) => {
  if (member?.payoneer_email) {
    return PaymentMethod.Payoneer;
  }

  if (member?.paypal_email) {
    return PaymentMethod.Paypal;
  }

  return PaymentMethod.Bank;
};

export const invoiceSourceLabels: Record<InvoiceSource, string> = {
  [InvoiceSource.Manual]: 'Manual',
  [InvoiceSource.Freelancer]: 'Freelancer',
  [InvoiceSource.Import]: 'Imported',
};

export const invoiceSources = [
  InvoiceSource.Manual,
  InvoiceSource.Freelancer,
  InvoiceSource.Import,
];

// The invoice filter dropdown is label-based; map the picked labels back to the
// values the API expects.
export const getInvoiceSourceByLabel = (label: string) =>
  invoiceSources.find((source) => invoiceSourceLabels[source] === label);

export const invoiceStatusLabels: Record<InvoiceStatus, string> = {
  [InvoiceStatus.PreReceived]: 'Pre-received',
  [InvoiceStatus.Received]: 'Received',
  [InvoiceStatus.Paid]: 'Paid',
};

export const paymentTypes = [
  {
    label: 'Domestic',
    value: paymentType.DOMESTIC,
  },
  {
    label: 'European',
    value: paymentType.EUROPEAN,
  },
  {
    label: 'International',
    value: paymentType.INTERNATIONAL,
  },
];

export const recipientTypes = [
  {
    label: 'Company',
    value: recipientType.COMPANY,
  },
  {
    label: 'Individual',
    value: recipientType.INDIVIDUAL,
  },
];

export const countries = countryList.map((country) => {
  return {
    label: country.name,
    value: country['alpha-2'],
  };
});

export const types = [
  {
    label: 'Full-time freelancer',
    value: 'Full-time freelancer',
  },
  {
    label: 'Freelancer',
    value: 'Freelancer',
  },
  {
    label: 'Actor',
    value: 'Actor',
  },
];

export const contractTypes = [
  {
    label: 'Terminated',
    value: 'Terminated',
  },
  {
    label: 'Indefinite',
    value: 'Indefinite',
  },
  {
    label: 'Author',
    value: 'Author',
  },
];

export const workSpaces = [
  {
    label: 'Home',
    value: 'Home',
  },
  {
    label: 'Youtube Studio',
    value: 'Youtube Studio',
  },
  {
    label: 'Facebook Studio',
    value: 'Facebook Studio',
  },
  {
    label: 'Filming location',
    value: 'Filming location',
  },
  {
    label: 'Other',
    value: 'Other',
  },
];

export const workFormats = [
  {
    label: 'Remote LT',
    value: 'Remote LT',
  },
  {
    label: 'Hybrid',
    value: 'Hybrid',
  },
  {
    label: 'Inhouse',
    value: 'Inhouse',
  },
  {
    label: 'Remote Abroad',
    value: 'Remote Abroad',
  },
];

export const statuses = [
  {
    label: 'Employed',
    value: FreelancerStatus.EMPLOYED,
  },
  {
    label: 'Unemployed',
    value: FreelancerStatus.UNEMPLOYED,
  },
];

export const employmentTypes = [
  {
    label: 'Full-time 1.5',
    value: 'Full-time 1.5',
  },
  {
    label: 'Full-time 1.25',
    value: 'Full-time 1.25',
  },
  {
    label: 'Full-time 1',
    value: 'Full-time 1',
  },
  {
    label: 'Full-time 0.75',
    value: 'Full-time 0.75',
  },
  {
    label: 'Full-time 0.625',
    value: 'Full-time 0.625',
  },
  {
    label: 'Full-time 0.5',
    value: 'Full-time 0.5',
  },
  {
    label: 'Full-time 0.33',
    value: 'Full-time 0.33',
  },
  {
    label: 'Full-time 0.25',
    value: 'Full-time 0.25',
  },
  {
    label: 'On-demand',
    value: 'On-demand',
  },
];

export const categories = [
  {
    label: 'Freelancer EN',
    value: 'Freelancer EN',
  },
  {
    label: 'Freelancer LT',
    value: 'Freelancer LT',
  },
  {
    label: 'Company',
    value: 'Company',
  },
  {
    label: 'Location',
    value: 'Location',
  },
];

export const genders = [
  {
    label: 'Male',
    value: 'Male',
  },
  {
    label: 'Female',
    value: 'Female',
  },
  {
    label: 'Other',
    value: 'Other',
  },
];

export const countryListNotRevolut = [
  { name: 'Afghanistan', 'alpha-2': 'AF', 'country-code': '004' },
  { name: 'Algeria', 'alpha-2': 'DZ', 'country-code': '012' },
  { name: 'Bahamas', 'alpha-2': 'BS', 'country-code': '044' },
  { name: 'Belarus', 'alpha-2': 'BY', 'country-code': '112' },
  {
    name: 'Bolivia (Plurinational State of)',
    'alpha-2': 'BO',
    'country-code': '068',
  },
  { name: 'Burkina Faso', 'alpha-2': 'BF', 'country-code': '854' },
  { name: 'Burundi', 'alpha-2': 'BI', 'country-code': '108' },
  { name: 'Central African Republic', 'alpha-2': 'CF', 'country-code': '140' },
  {
    name: 'Congo, Democratic Republic of the',
    'alpha-2': 'CD',
    'country-code': '180',
  },
  { name: "Côte d'Ivoire", 'alpha-2': 'CI', 'country-code': '384' },
  { name: 'Cuba', 'alpha-2': 'CU', 'country-code': '192' },
  { name: 'Egypt', 'alpha-2': 'EG', 'country-code': '818' },
  { name: 'Eritrea', 'alpha-2': 'ER', 'country-code': '232' },
  { name: 'Ethiopia', 'alpha-2': 'ET', 'country-code': '231' },
  { name: 'Guinea', 'alpha-2': 'GN', 'country-code': '324' },
  { name: 'Guinea-Bissau', 'alpha-2': 'GW', 'country-code': '624' },
  { name: 'Guyana', 'alpha-2': 'GY', 'country-code': '328' },
  { name: 'Haiti', 'alpha-2': 'HT', 'country-code': '332' },
  { name: 'Iraq', 'alpha-2': 'IQ', 'country-code': '368' },
  {
    name: 'Iran (Islamic Republic of)',
    'alpha-2': 'IR',
    'country-code': '364',
  },
  { name: 'Cambodia', 'alpha-2': 'KH', 'country-code': '116' },
  {
    name: "Lao People's Democratic Republic",
    'alpha-2': 'LA',
    'country-code': '418',
  },
  { name: 'Lebanon', 'alpha-2': 'LB', 'country-code': '422' },
  { name: 'Liberia', 'alpha-2': 'LR', 'country-code': '430' },
  { name: 'Libya', 'alpha-2': 'LY', 'country-code': '434' },
  { name: 'Mozambique', 'alpha-2': 'MZ', 'country-code': '508' },
  { name: 'Myanmar', 'alpha-2': 'MM', 'country-code': '104' },
  { name: 'Nigeria', 'alpha-2': 'NG', 'country-code': '566' },
  { name: 'Panama', 'alpha-2': 'PA', 'country-code': '591' },
  { name: 'Palestine, State of', 'alpha-2': 'PS', 'country-code': '275' },
  { name: 'Palau', 'alpha-2': 'PW', 'country-code': '585' },
  { name: 'Russian Federation', 'alpha-2': 'RU', 'country-code': '643' },
  { name: 'Sierra Leone', 'alpha-2': 'SL', 'country-code': '694' },
  { name: 'Somalia', 'alpha-2': 'SO', 'country-code': '706' },
  { name: 'South Sudan', 'alpha-2': 'SS', 'country-code': '728' },
  { name: 'Sri Lanka', 'alpha-2': 'LK', 'country-code': '144' },
  { name: 'Sudan', 'alpha-2': 'SD', 'country-code': '729' },
  { name: 'Syrian Arab Republic', 'alpha-2': 'SY', 'country-code': '760' },
  { name: 'Tajikistan', 'alpha-2': 'TJ', 'country-code': '762' },
  { name: 'Timor-Leste', 'alpha-2': 'TL', 'country-code': '626' },
  { name: 'Tunisia', 'alpha-2': 'TN', 'country-code': '788' },
  { name: 'Uganda', 'alpha-2': 'UG', 'country-code': '800' },
  {
    name: 'Venezuela (Bolivarian Republic of)',
    'alpha-2': 'VE',
    'country-code': '862',
  },
  { name: 'Vanuatu', 'alpha-2': 'VU', 'country-code': '548' },
  { name: 'Yemen', 'alpha-2': 'YE', 'country-code': '887' },
  { name: 'Zimbabwe', 'alpha-2': 'ZW', 'country-code': '716' },
];

export const countriesNotRevolut = countryListNotRevolut.map((country) => {
  return {
    label: country.name,
    value: country['alpha-2'],
  };
});

export const debtorBanks = [
  {
    label: 'Revolut',
    value: 'Revolut',
  },
  {
    label: 'Swedbank',
    value: 'Swedbank',
  },
];

export const banks = [
  { country: 'LT', bic: 'HABALT22', bank: 'Swedbank', code: ['73000'] },
  { country: 'LT', bic: 'CBVILT2', bank: 'SEB', code: ['70440'] },
  {
    country: 'LT',
    bic: 'AGBLLT2',
    bank: 'Luminor Bank',
    code: ['40100', '21400'],
  },
  {
    country: 'LT',
    bic: 'CBSBLT26',
    bank: 'Artea bankas',
    code: [
      '71800',
      '71812',
      '71821',
      '71816',
      '71825',
      '71899',
      '71819',
      '71826',
      '71809',
      '71823',
      '71802',
      '71805',
      '71829',
      '71804',
      '71818',
      '71808',
      '71806',
      '71815',
      '71820',
      '71814',
      '71830',
      '71828',
      '71807',
      '71813',
      '71817',
      '71824',
      '71811',
      '71810',
      '71803',
      '71822',
      '71827',
      '70500',
    ],
  },
  { country: 'LT', bic: 'INDULT2', bank: 'Citadele', code: ['72900'] },
  { country: 'LT', bic: 'MDBALT22', bank: 'Urbo bankas', code: ['72300'] },
  {
    country: 'LT',
    bic: 'EVIULT2V',
    bank: 'Paysera LT',
    code: ['35000'],
  },
  {
    country: 'LT',
    bic: 'LIABLT2XCON',
    bank: 'Lietuvos banko padalinys',
    code: ['10110'],
  },
  {
    country: 'LT',
    bic: 'LIABLT2XDCA',
    bank: 'Lietuvos banko TIPS DCA',
    code: [],
  },
  { country: 'LT', bic: 'LIABLT2XECM', bank: 'Lietuvos banko ECM', code: [] },
  {
    country: 'LT',
    bic: 'LIABLT2XPAY',
    bank: "CENTROlink sistema TIPS'e",
    code: [],
  },
  {
    country: 'LT',
    bic: 'LIABLT2XSNO',
    bank: 'Lietuvos banko sąskaita BAB SNORAS įmokoms surinkti',
    code: ['00758'],
  },
  {
    country: 'LT',
    bic: 'LIABLT2',
    bank: 'Lietuvos bankas',
    code: ['10100', '10101', '10900', '10110'],
  },
  {
    country: 'LT',
    bic: 'CSDLLT22RTG',
    bank: 'Lietuvos centrinis vertybinių popierių depozitoriumas',
    code: ['90002'],
  },
  {
    country: 'LT',
    bic: 'CSDLLT22',
    bank: 'Lietuvos centrinis vertybinių popierių depozitoriumas',
    code: ['90001'],
  },
  { country: 'LT', bic: 'LIABLT2XMMS', bank: 'CENTROlink INST', code: [] },
  {
    country: 'LT',
    bic: 'AGBLLT2XTGT',
    bank: 'Luminor Bank AS Lietuvos sk. minimum rezerves',
    code: ['40199'],
  },
  {
    country: 'LT',
    bic: 'BIGKLT21',
    bank: 'BIGBANK AS filialas',
    code: ['20900'],
  },
  { country: 'LT', bic: 'CRUBLT22', bank: 'Crius LT, UAB', code: ['39807'] },
  {
    country: 'LT',
    bic: 'EUEBLT22',
    bank: 'European Merchant Bank UAB',
    code: ['71100'],
  },
  {
    country: 'LT',
    bic: 'FEMAMTMA',
    bank: 'Multitude Bank PLC',
    code: ['30066'],
  },
  { country: 'LT', bic: 'FIBALT22', bank: 'AB bankas "Finasta"', code: [] },
  { country: 'LT', bic: 'FJORLT21', bank: 'AB "Fjord Bank"', code: ['22500'] },
  { country: 'LT', bic: 'FNRKLT22', bank: 'Finora Bank UAB', code: ['73400'] },
  { country: 'LT', bic: 'GRFNLT21', bank: 'UAB GF bankas', code: ['60024'] },
  {
    country: 'LT',
    bic: 'IBFLLT22',
    bank: '"AS Inbank filialas"',
    code: ['22400'],
  },
  { country: 'LT', bic: 'KUSRLT21', bank: 'AB "Mano bankas"', code: ['50300'] },
  {
    country: 'LT',
    bic: 'KUSRLT24',
    bank: 'AB "Mano bankas" - 2',
    code: ['50301'],
  },
  {
    country: 'LT',
    bic: 'MULTLT2',
    bank: 'AS "Industra Bank" Lietuvos filialas',
    code: ['21700'],
  },
  {
    country: 'LT',
    bic: 'NDEALT2',
    bank: 'Luminor Bank',
    code: ['21499'],
  },
  {
    country: 'LT',
    bic: 'NORWNOK1',
    bank: 'Bank Norwegian, en filial av NOBA Bank Group AB (publ)',
    code: ['30021'],
  },
  {
    country: 'LT',
    bic: 'OKOYLT2',
    bank: 'OP Corporate Bank plc Lietuvos filialas',
    code: ['21500'],
  },
  { country: 'LT', bic: 'PYYRLT22', bank: 'PayRay Bank, UAB', code: ['22600'] },
  { country: 'LT', bic: 'RETBLT21', bank: 'Revolut Bank UAB', code: ['70700'] },
  {
    country: 'LT',
    bic: 'REVOFRP2',
    bank: 'REVOLUT FRANCE SUCCURSALE DE REVOLUT BANK UAB',
    code: ['32501'],
  },
  {
    country: 'LT',
    bic: 'REVOLT21',
    bank: 'Revolut Bank UAB (Payments)',
    code: ['32500'],
  },
  { country: 'LT', bic: 'RVUALT2V', bank: 'Revolut Bank UAB', code: ['70701'] },
  {
    country: 'LT',
    bic: 'SMPOLT22',
    bank: 'Danske Bank A/S Lietuvos filialas',
    code: ['74000'],
  },
  { country: 'LT', bic: 'TBIBBGSF', bank: 'TBI Bank EAD', code: ['30069'] },
  {
    country: 'LT',
    bic: 'UASNFIH2',
    bank: 'SALDO BANK UAB filialas Suomijoje',
    code: ['39913'],
  },
  { country: 'LT', bic: 'UASNLT22', bank: 'Saldo Bank UAB', code: ['73300'] },
  { country: 'LT', bic: 'USDFLT22', bank: 'UAB SME Bank', code: ['74100'] },
  { country: 'LT', bic: 'USDFLT23', bank: 'UAB SME Bank', code: ['74101'] },
  {
    country: 'LT',
    bic: 'VRKULT21',
    bank: 'RATO bankas, UAB',
    code: ['50160', '51900'],
  },
  {
    country: 'LT',
    bic: 'JCKULT21',
    bank: 'Jungtinė centrinė kredito unija',
    code: [
      '50109',
      '50121',
      '50126',
      '50120',
      '50164',
      '50144',
      '50147',
      '50200',
      '50114',
      '50117',
      '50139',
      '50129',
      '50162',
    ],
  },
  {
    country: 'LT',
    bic: 'JCKULT21X99',
    bank: 'Jungtinė centrinė kredito unija',
    code: ['50299'],
  },
  {
    country: 'LT',
    bic: 'LCKULT22',
    bank: 'Lietuvos centrinė kredito unija',
    code: [
      '50152',
      '50130',
      '50111',
      '50149',
      '50142',
      '50132',
      '50133',
      '50127',
      '50159',
      '50122',
      '50163',
      '50108',
      '50125',
      '50106',
      '50153',
      '50113',
      '50168',
      '50154',
      '50166',
      '50123',
      '50101',
      '50500',
      '50135',
      '50112',
      '50138',
      '50100',
      '50146',
      '50140',
      '50119',
      '50110',
      '50124',
      '50105',
      '50156',
      '50165',
      '50102',
      '50115',
      '50128',
      '50131',
      '50151',
      '50134',
      '50145',
      '50136',
      '50104',
      '50167',
      '50118',
    ],
  },
  {
    country: 'LT',
    bic: 'CSDLLT22577',
    bank: 'Nasdaq CSD SE Lietuvos filialas',
    code: ['90577'],
  },
  {
    country: 'LT',
    bic: 'CSDLXE22',
    bank: 'Nasdaq CSD Lietuvos filialas antrai 90577 DT2S sąskaitai',
    code: [],
  },
  {
    country: 'LT',
    bic: 'UAMTLT22',
    bank: 'UAB Uniqum capital',
    code: ['91004'],
  },
  {
    country: 'LT',
    bic: 'CRUGLT21',
    bank: 'Kredito unija "Saulėgrąža"',
    code: ['51700'],
  },
  {
    country: 'LT',
    bic: 'KRUALT21',
    bank: 'Kredito unija "Taupa"',
    code: ['59999'],
  },
  {
    country: 'LT',
    bic: 'KRUZLT21',
    bank: 'Kredito unija Zanavykų bankelis',
    code: ['52400'],
  },
  {
    country: 'LT',
    bic: 'KUVKLT21',
    bank: 'Kredito unija "Vilniaus kreditas"',
    code: ['50600'],
  },
  {
    country: 'LT',
    bic: 'LTKULT21',
    bank: 'LTL Kredito unija',
    code: ['51500'],
  },
  {
    country: 'LT',
    bic: 'SIKNLT21',
    bank: 'Kredito unija "Baltija"',
    code: ['50150'],
  },
  {
    country: 'LT',
    bic: 'UCTKLT21',
    bank: '"Kredito unija Centro taupomoji kasa"',
    code: ['50400'],
  },
  {
    country: 'LT',
    bic: 'VEKULT21',
    bank: 'Taupkasė, kredito unija',
    code: ['51600'],
  },
  { country: 'LT', bic: 'ADWSGB22', bank: 'MONEVIUM Ltd', code: ['30086'] },
  { country: 'LT', bic: 'AKELEE21', bank: 'AS Pocopay', code: ['38100'] },
  {
    country: 'LT',
    bic: 'ARPYGB21',
    bank: 'Prune Payments Ltd',
    code: ['30039'],
  },
  { country: 'LT', bic: 'BEFKCZP1', bank: 'B-Efekt a.s.', code: ['30040'] },
  { country: 'LT', bic: 'BITSNL2A', bank: 'Yoursafe B.V.', code: ['30042'] },
  { country: 'LT', bic: 'BRTESESS', bank: 'Brite AB', code: ['39904'] },
  {
    country: 'LT',
    bic: 'BUEEGB22',
    bank: 'Bureau Buttercrane Ltd',
    code: ['30081'],
  },
  { country: 'LT', bic: 'CIURGB21', bank: 'Cauri Ltd', code: ['39800'] },
  {
    country: 'LT',
    bic: 'CNENPLP1',
    bank: 'CURRENCY ONE S.A.',
    code: ['30079'],
  },
  {
    country: 'LT',
    bic: 'CNNNGB21',
    bank: 'Connectum Limited',
    code: ['30030'],
  },
  {
    country: 'LT',
    bic: 'CPAYIE2D',
    bank: 'Fire Financial Services Limited EU',
    code: ['30096'],
  },
  {
    country: 'LT',
    bic: 'CPAYIE31',
    bank: 'Fire Financial Services Limited UK',
    code: ['30097'],
  },
  { country: 'LT', bic: 'DIGEBEB2', bank: 'Digiteal SA', code: ['30012'] },
  {
    country: 'LT',
    bic: 'DSFELT21',
    bank: 'DSBC Financial Europe, UAB',
    code: ['32700'],
  },
  {
    country: 'LT',
    bic: 'EAPFESM2',
    bank: 'Prisma Payments EP SA',
    code: ['30093'],
  },
  {
    country: 'LT',
    bic: 'EXCHESMM',
    bank: 'MACCORP EXACT CHANGE, E.P., S.A.',
    code: ['30015'],
  },
  {
    country: 'LT',
    bic: 'FFCSCZP1',
    bank: 'FAS finance company s.r.o.',
    code: ['30076'],
  },
  { country: 'LT', bic: 'FINDMTMT', bank: 'Finductive Ltd', code: ['30080'] },
  {
    country: 'LT',
    bic: 'GFCBEE22',
    bank: 'GFC Good Finance Company AS',
    code: ['39400'],
  },
  {
    country: 'LT',
    bic: 'HOLVATW2',
    bank: 'Holvi Payment Services Oy Zweigniederlassung Österreich',
    code: ['39922'],
  },
  {
    country: 'LT',
    bic: 'HOLVDEB1',
    bank: 'Holvi Payment Services Oy - ZWG Deutschland',
    code: ['30016'],
  },
  {
    country: 'LT',
    bic: 'HOLVFIHH',
    bank: 'Holvi Payment Services Oy',
    code: ['30017'],
  },
  { country: 'LT', bic: 'IVPYFIH2', bank: 'Ivy Pay Oy', code: ['39921'] },
  {
    country: 'LT',
    bic: 'MAEHEE21',
    bank: 'Maaelu Edendamise Hoiu-laenuuhistu',
    code: ['30071'],
  },
  {
    country: 'LT',
    bic: 'MAFVGB21',
    bank: 'Mayzus Financial Services Limited',
    code: ['39900'],
  },
  {
    country: 'LT',
    bic: 'MCPIMTM1',
    bank: 'Money+Card Payment Institution Ltd',
    code: ['30052'],
  },
  { country: 'LT', bic: 'MOIULT21', bank: 'Bruc Bond, UAB', code: ['35800'] },
  { country: 'LT', bic: 'OPEXGB2L', bank: 'OPENAX LIMITED', code: ['30085'] },
  {
    country: 'LT',
    bic: 'PAERCZP1',
    bank: 'Payment Execution s.r.o.',
    code: ['30041'],
  },
  { country: 'LT', bic: 'PAUALT22', bank: 'UAB "Paystrax"', code: ['34300'] },
  {
    country: 'LT',
    bic: 'PHPYMTM1',
    bank: 'Phoenix Payments Ltd',
    code: ['30070'],
  },
  {
    country: 'LT',
    bic: 'PYBLCY2L',
    bank: 'Payabl. CY Limited',
    code: ['39905'],
  },
  { country: 'LT', bic: 'PYYPGB21', bank: 'MY EU PAY LTD', code: ['30083'] },
  {
    country: 'LT',
    bic: 'POWHCY22',
    bank: 'Payabl. CY Limited',
    code: ['30098'],
  },
  {
    country: 'LT',
    bic: 'RILALT22',
    bank: 'RIA Lithuania UAB',
    code: ['39814'],
  },
  {
    country: 'LT',
    bic: 'SSOPPLP2',
    bank: 'Speedy AG sp. Z o.o.',
    code: ['39916'],
  },
  { country: 'LT', bic: 'TBFULT21', bank: 'TBF Finance, UAB', code: ['31800'] },
  { country: 'LT', bic: 'TFIMCY2N', bank: 'TFI ECOMMPAY LTD', code: ['30018'] },
  {
    country: 'LT',
    bic: 'TFIMGRAA',
    bank: 'TFI Markets Limited Greek Branch',
    code: ['30008'],
  },
  { country: 'LT', bic: 'TPAYSKB', bank: 'Trust Pay, a.s.', code: ['30056'] },
  {
    country: 'LT',
    bic: 'TRPEMTM1',
    bank: 'TRUEVO PAYMENTS LTD',
    code: ['30054'],
  },
  { country: 'LT', bic: 'TRWIBEB1', bank: 'Wise Europe SA', code: ['30073'] },
  { country: 'LT', bic: 'UACOLT21', bank: 'UAB Contabilita', code: ['31000'] },
  { country: 'LT', bic: 'UADRLT21', bank: 'UAB Drauskaita', code: ['31500'] },
  { country: 'LT', bic: 'UALTLT22', bank: 'UAB Lock Trust', code: ['39801'] },
  { country: 'LT', bic: 'UANOLT22', bank: 'AlphaPay, UAB', code: ['39808'] },
  {
    country: 'LT',
    bic: 'UAPYLT21',
    bank: 'UAB "Alternative Payments"',
    code: ['35300'],
  },
  {
    country: 'LT',
    bic: 'UHISEE21',
    bank: 'Uhisarveldused AS',
    code: ['30074'],
  },
  {
    country: 'LT',
    bic: 'UNNIGB21',
    bank: 'UNIFIED FINANCE LTD',
    code: ['30061'],
  },
  { country: 'LT', bic: 'VAAALT21', bank: 'ValorPay, UAB', code: ['33100'] },
  { country: 'LT', bic: 'AMPULT22', bank: 'Newrails UAB', code: ['34900'] },
  {
    country: 'LT',
    bic: 'APSLLT21',
    bank: 'UAB "ANTIGRAVITY PAYMENT SYSTEMS"',
    code: ['30100'],
  },
  { country: 'LT', bic: 'BAXXLT22', bank: 'Finci, UAB', code: ['34200'] },
  { country: 'LT', bic: 'BEMULT22', bank: 'Benker UAB', code: ['39826'] },
  {
    country: 'LT',
    bic: 'BIYSGB2A',
    bank: 'Bilderlings Pay Limited',
    code: ['30035'],
  },
  { country: 'LT', bic: 'BZENLT22', bank: 'UAB ZEN.COM', code: ['31300'] },
  { country: 'LT', bic: 'CARDCY2L', bank: 'UNLIMIT EU LTD', code: ['30034'] },
  { country: 'LT', bic: 'CBNULT2V', bank: 'CBI Money UAB', code: ['34100'] },
  {
    country: 'LT',
    bic: 'CFTEMTM1',
    bank: 'OpenPayd Financial Services Malta Limited',
    code: ['30027'],
  },
  { country: 'LT', bic: 'CFZZGB21', bank: 'CFS-Zipp Limited', code: ['30043'] },
  {
    country: 'LT',
    bic: 'CLJUGB21',
    bank: 'Clear Junction Limited',
    code: ['39500'],
  },
  { country: 'LT', bic: 'CLUALT22', bank: 'Clearshift, UAB', code: ['34700'] },
  { country: 'LT', bic: 'CNUALT21', bank: 'UAB "ConnectPay"', code: ['37400'] },
  {
    country: 'LT',
    bic: 'CRVULT21',
    bank: 'Corporate Services UAB',
    code: ['39804'],
  },
  {
    country: 'LT',
    bic: 'CUXRIE2D',
    bank: 'Currenxie Technologies Limited',
    code: ['39920'],
  },
  { country: 'LT', bic: 'DIUALT22', bank: 'DiPocket UAB', code: ['39818'] },
  { country: 'LT', bic: 'DMVOLT22', bank: 'Demivolt, UAB', code: ['39806'] },
  {
    country: 'LT',
    bic: 'EAPSBGS2',
    bank: 'EASY PAYMENT SERVICES LTD',
    code: ['30023'],
  },
  { country: 'LT', bic: 'ECBXCY2N', bank: 'ECOMMBX LIMITED', code: ['37900'] },
  { country: 'LT', bic: 'EMONMTM2', bank: 'Emoney P.L.C.', code: ['30031'] },
  {
    country: 'LT',
    bic: 'EPMTGB2LLTH',
    bank: 'Epayments Systems Limited',
    code: ['30045'],
  },
  { country: 'LT', bic: 'EPUALT22', bank: 'UAB "epayblock"', code: ['33500'] },
  { country: 'LT', bic: 'EXUALT22', bank: 'TT Express UAB', code: ['39822'] },
  {
    country: 'LT',
    bic: 'FAPOCZP1',
    bank: 'Fairplay Pay s.r.o.',
    code: ['30046'],
  },
  { country: 'LT', bic: 'FNCULT22', bank: 'FINCAUSE, UAB', code: ['39827'] },
  {
    country: 'LT',
    bic: 'FNHOGB21',
    bank: 'FINANCIAL HOUSE LIMITED',
    code: ['30090'],
  },
  { country: 'LT', bic: 'FOUNGB21', bank: 'Monetley LTD', code: ['30091'] },
  {
    country: 'LT',
    bic: 'FTCSSESS',
    bank: 'Intergiro Intl AB (publ)',
    code: ['30028'],
  },
  { country: 'LT', bic: 'GLUALT22', bank: 'Payswix, UAB', code: ['38800'] },
  { country: 'LT', bic: 'GUAVGB22', bank: 'Guavapay Limited', code: ['30092'] },
  { country: 'LT', bic: 'GUPULT22', bank: 'UAB Guru Pay', code: ['34000'] },
  { country: 'LT', bic: 'HRSAFR22', bank: 'Heuro SAS', code: ['30009'] },
  { country: 'LT', bic: 'IBIULT21', bank: 'ibsettle UAB', code: ['39100'] },
  { country: 'LT', bic: 'IHPYEE22', bank: 'inHouse Pay AS', code: ['30026'] },
  { country: 'LT', bic: 'IIPALV22', bank: 'PACE FS SIA', code: ['39923'] },
  {
    country: 'LT',
    bic: 'INHULT21',
    bank: 'International Fintech, UAB',
    code: ['36400'],
  },
  { country: 'LT', bic: 'INTFBGSF', bank: 'iCARD AD', code: ['30053'] },
  {
    country: 'LT',
    bic: 'ISEMCY22FLY',
    bank: 'ISX Financial EU PLC FLY',
    code: ['30138'],
  },
  {
    country: 'LT',
    bic: 'ISEMCY22',
    bank: 'ISX Financial EU PLC',
    code: ['30038'],
  },
  {
    country: 'LT',
    bic: 'LIPYLT22',
    bank: 'UAB "Lux International Payment System"',
    code: ['39825'],
  },
  {
    country: 'LT',
    bic: 'MAABLT21',
    bank: 'UAB Majestic Financial',
    code: ['37300'],
  },
  {
    country: 'LT',
    bic: 'MIEGLT21',
    bank: '"Secure Nordic Payments", UAB',
    code: ['35100'],
  },
  {
    country: 'LT',
    bic: 'MLLKMTMT',
    bank: 'MELLIFERA KARTIERA LIMITED',
    code: ['39918'],
  },
  {
    country: 'LT',
    bic: 'MNNELT21',
    bank: 'UAB "Maneuver LT"',
    code: ['31100'],
  },
  {
    country: 'LT',
    bic: 'MODRESB2',
    bank: 'Modulr Finance B.V., Spain Branch',
    code: ['30010'],
  },
  {
    country: 'LT',
    bic: 'MODRFRP2',
    bank: 'Modulr Finance B.V., France Branch',
    code: ['30011'],
  },
  {
    country: 'LT',
    bic: 'MODRGB23',
    bank: 'MODULR FS LIMITED',
    code: ['30029'],
  },
  {
    country: 'LT',
    bic: 'MODRIE22',
    bank: 'Modulr Finance B.V., Ireland Branch',
    code: ['30129'],
  },
  {
    country: 'LT',
    bic: 'MODRNL22',
    bank: 'Modulr Finance B.V., Netherlands Branch',
    code: ['30229'],
  },
  {
    country: 'LT',
    bic: 'MOKZLT21',
    bank: 'UAB "MOKILIZINGAS"',
    code: ['60016'],
  },
  { country: 'LT', bic: 'MOOWGB22', bank: 'Moorwand LTD', code: ['30084'] },
  { country: 'LT', bic: 'MOTOLT21', bank: 'Foxpay, UAB', code: ['30500'] },
  { country: 'LT', bic: 'MSBBGB2L', bank: 'BRAZAUK LTD', code: ['30067'] },
  {
    country: 'LT',
    bic: 'MSFVMTM1',
    bank: 'CEEVO Financial Services (Malta) Limited',
    code: ['30082'],
  },
  { country: 'LT', bic: 'MTCCMTMT', bank: 'MTACC LTD', code: ['30095'] },
  {
    country: 'LT',
    bic: 'NARYFIH2',
    bank: 'Narvi Payments Oy Ab',
    code: ['39912'],
  },
  { country: 'LT', bic: 'NEUALT21', bank: 'UAB "NexPay"', code: ['30800'] },
  { country: 'LT', bic: 'NIKULT22', bank: 'Nikulipe UAB', code: ['34600'] },
  { country: 'LT', bic: 'NSPYLT22', bank: 'UAB "NS Pay"', code: ['39828'] },
  {
    country: 'LT',
    bic: 'OKEOLT22',
    bank: 'Okeo Payments, UAB',
    code: ['39805'],
  },
  {
    country: 'LT',
    bic: 'OPPFFRP2',
    bank: 'OpenPayd Financial Services Malta',
    code: ['30007'],
  },
  {
    country: 'LT',
    bic: 'OPTULT22',
    bank: 'Opal Transfer EU, UAB',
    code: ['33800'],
  },
  {
    country: 'LT',
    bic: 'ORRRCZP1',
    bank: 'OrangeTrust s.r.o.',
    code: ['30051'],
  },
  { country: 'LT', bic: 'PAEDGB21', bank: 'PAYSEND PLC', code: ['30047'] },
  {
    country: 'LT',
    bic: 'PAEOLT21',
    bank: 'UAB PanPay Europe',
    code: ['38700'],
  },
  { country: 'LT', bic: 'PAYVBEB2', bank: 'Paynovate S.A.', code: ['30025'] },
  { country: 'LT', bic: 'PAOMGB21', bank: 'Payoma Limited', code: ['38600'] },
  { country: 'LT', bic: 'PAPYMTMT', bank: 'Papaya Ltd', code: ['38200'] },
  { country: 'LT', bic: 'PATCBGSF', bank: 'Paynetics AD', code: ['30064'] },
  { country: 'LT', bic: 'PAUUMTM1', bank: 'FinXP Limited', code: ['30055'] },
  {
    country: 'LT',
    bic: 'PCTULT21',
    bank: 'PCS Transfer, UAB',
    code: ['31400'],
  },
  { country: 'LT', bic: 'PELDMTM2', bank: 'PayDo EU Ltd.', code: ['39915'] },
  {
    country: 'LT',
    bic: 'PEPGLT21',
    bank: 'UAB "Perlas Finance"',
    code: ['30200'],
  },
  {
    country: 'LT',
    bic: 'PFSRIE21',
    bank: 'PFS Card Services Ireland Limited',
    code: ['30032'],
  },
  {
    country: 'LT',
    bic: 'PFSSESM1',
    bank: 'PFS Card Services Ireland Limited Succursal Espana',
    code: ['30232'],
  },
  { country: 'LT', bic: 'PYYTGB21', bank: 'PAYSTREE LTD', code: ['30077'] },
  {
    country: 'LT',
    bic: 'PYMXMTMT',
    bank: 'Finance Incorporated Limited',
    code: ['30065'],
  },
  { country: 'LT', bic: 'PMNTLT2V', bank: 'PAYMONT, UAB', code: ['39815'] },
  {
    country: 'LT',
    bic: 'PRNSFRP1',
    bank: 'PFS Card Services Ireland Limited (FR branch)',
    code: ['30132'],
  },
  { country: 'LT', bic: 'PWSRSI22', bank: 'PAYWISER D.O.O.', code: ['39906'] },
  { country: 'LT', bic: 'REVOGB21', bank: 'Revolut LTD', code: ['39200'] },
  {
    country: 'LT',
    bic: 'SEOUGB21',
    bank: 'Settlego Solutions Limited',
    code: ['30068'],
  },
  {
    country: 'LT',
    bic: 'SEPMCY2N',
    bank: 'SEPAGA E.M.I. LIMITED',
    code: ['30063'],
  },
  {
    country: 'LT',
    bic: 'SESOLT21',
    bank: 'UAB "Seven Seas Europe"',
    code: ['37100'],
  },
  {
    country: 'LT',
    bic: 'SHFRLT21',
    bank: 'SHIFT Financial Services LT, UAB',
    code: ['31700'],
  },
  {
    country: 'LT',
    bic: 'SYPLMTM2',
    bank: 'SYSTEM PAY SERVICES (MALTA) LIMITED',
    code: ['39911'],
  },
  {
    country: 'LT',
    bic: 'SONCLT21',
    bank: 'UAB "SONECT Europe"',
    code: ['33200'],
  },
  { country: 'LT', bic: 'STUALT21', bank: 'SatchelPay, UAB', code: ['30600'] },
  { country: 'LT', bic: 'SUMUIE22', bank: 'SumUp Limited', code: ['30022'] },
  {
    country: 'LT',
    bic: 'SUPAGB21',
    bank: 'SumUp Payments Limited',
    code: ['30089'],
  },
  {
    country: 'LT',
    bic: 'SUPULT22',
    bank: 'SumUp EU Payments, UAB',
    code: ['33700'],
  },
  { country: 'LT', bic: 'SVVVSESS', bank: 'Steven AB', code: ['39910'] },
  { country: 'LT', bic: 'TEUALT22', bank: 'UAB TeslaPay', code: ['31900'] },
  { country: 'LT', bic: 'TPROLV22', bank: 'SIA Transact Pro', code: ['39700'] },
  {
    country: 'LT',
    bic: 'TRYULT21',
    bank: 'Transactive Systems UAB',
    code: ['37800'],
  },
  {
    country: 'LT',
    bic: 'TRLULT21',
    bank: 'TransferGo Lithuania UAB',
    code: ['32400'],
  },
  { country: 'LT', bic: 'TRUDBG21', bank: 'PayNovus AD', code: ['30036'] },
  {
    country: 'LT',
    bic: 'TRWIGB22',
    bank: 'Wise Payments Ltd',
    code: ['39000'],
  },
  { country: 'LT', bic: 'UAAALT22', bank: 'UAB Hokodo', code: ['39810'] },
  { country: 'LT', bic: 'UAAMLT21', bank: 'Contomobile UAB', code: ['35400'] },
  { country: 'LT', bic: 'UABELT22', bank: 'Bebawa, UAB', code: ['39802'] },
  {
    country: 'LT',
    bic: 'UADELT21',
    bank: 'UAB "deVere E-Money"',
    code: ['36600'],
  },
  { country: 'LT', bic: 'UAINLT21', bank: 'UAB "NIUM EU"', code: ['35900'] },
  { country: 'LT', bic: 'UALELT22', bank: 'UAB BLUE EMI LT', code: ['39823'] },
  { country: 'LT', bic: 'UAMMLT21', bank: 'UAB "Pace App"', code: ['36000'] },
  {
    country: 'LT',
    bic: 'UANFLT21',
    bank: '"NEO Finance", AB',
    code: ['35200'],
  },
  {
    country: 'LT',
    bic: 'UANILT22',
    bank: 'UAB Nandi Finance',
    code: ['34500'],
  },
  { country: 'LT', bic: 'UAPALT22', bank: 'PAYRNET UAB', code: ['39809'] },
  { country: 'LT', bic: 'UAPELT22', bank: 'UAB "Pervesk"', code: ['35500'] },
  {
    country: 'LT',
    bic: 'UAPLLT21',
    bank: 'UAB "Pyrros Lithuania"',
    code: ['36700'],
  },
  {
    country: 'LT',
    bic: 'UAPPLT21',
    bank: 'UAB "PHOENIX PAYMENTS"',
    code: ['32200'],
  },
  {
    country: 'LT',
    bic: 'UAPULT22',
    bank: 'UAB "PAYTEND EUROPE"',
    code: ['31200'],
  },
  { country: 'LT', bic: 'UATULT22', bank: 'UAB Travel Union', code: ['34800'] },
  { country: 'LT', bic: 'UAUPLT2V', bank: 'Paylar, UAB', code: ['33400'] },
  { country: 'LT', bic: 'UAWALT21', bank: 'UAB "Walletto"', code: ['30900'] },
  { country: 'LT', bic: 'UAWALT22', bank: 'UAB "Walletto"', code: ['30901'] },
  { country: 'LT', bic: 'UDRULT22', bank: 'UAB EXCHANGELT', code: ['39819'] },
  {
    country: 'LT',
    bic: 'UFPOLT21',
    bank: 'UAB "Finansinės paslaugos "Contis"',
    code: ['39600'],
  },
  {
    country: 'LT',
    bic: 'UFUNLT22',
    bank: 'UAB Finolita Unio',
    code: ['35600'],
  },
  {
    country: 'LT',
    bic: 'UIPULT21',
    bank: 'UAB International Payment Union',
    code: ['32300'],
  },
  { country: 'LT', bic: 'UNECLT2V', bank: 'UAB NEOCARD', code: ['38900'] },
  {
    country: 'LT',
    bic: 'URFSLT21',
    bank: 'UAB "Rebellion Fintech Services"',
    code: ['32600'],
  },
  { country: 'LT', bic: 'USPELT2V', bank: 'UAB "Nuvei"', code: ['33900'] },
  {
    country: 'LT',
    bic: 'USTILT2V',
    bank: 'UAB Stanhope Financial',
    code: ['39811'],
  },
  { country: 'LT', bic: 'VAUALT21', bank: 'Valyuz, UAB', code: ['30700'] },
  {
    country: 'LT',
    bic: 'VEPALT2',
    bank: 'Verified Payments, UAB',
    code: ['37500'],
  },
  { country: 'LT', bic: 'VIPULT22', bank: 'Via Payments UAB', code: ['35700'] },
  { country: 'LT', bic: 'VPLALT21', bank: 'UAB Etapay', code: ['31600'] },
  { country: 'LT', bic: 'WAUALT21', bank: 'Wallter, UAB', code: ['32000'] },
  { country: 'LT', bic: 'WIUALT22', bank: 'Wittix, UAB', code: ['33000'] },
  {
    country: 'LT',
    bic: 'MFRLLT22',
    bank: 'Lietuvos Respublikos finansų ministerija',
    code: ['40400'],
  },
  {
    country: 'AT',
    bic: 'BKAUATWW',
    bank: 'UniCredit Bank Austria AG',
    code: [],
  },
  { country: 'AT', bic: 'GIBAATWW', bank: 'Erste Group Bank AG', code: [] },
  {
    country: 'AT',
    bic: 'RZBAATWW',
    bank: 'Raiffeisen Bank International AG',
    code: [],
  },
  { country: 'AT', bic: 'BAWAATWW', bank: 'BAWAG P.S.K.', code: [] },
  { country: 'AT', bic: 'OBKLAT2L', bank: 'Oberbank AG', code: [] },
  { country: 'AT', bic: 'BFKKAT2K', bank: 'BKS Bank AG', code: [] },
  { country: 'AT', bic: 'HYPNATWW', bank: 'Hypo Noe Landesbank', code: [] },
  {
    country: 'AT',
    bic: 'SPLSAT21',
    bank: 'Sparkasse Oberösterreich',
    code: [],
  },
  { country: 'AT', bic: 'TVBAAT2S', bank: 'Tiroler Sparkasse', code: [] },
  { country: 'BE', bic: 'GEBABEBB', bank: 'BNP Paribas Fortis', code: [] },
  { country: 'BE', bic: 'KREDBEBB', bank: 'KBC Bank', code: [] },
  { country: 'BE', bic: 'BBRUBEBB', bank: 'ING Belgium', code: [] },
  { country: 'BE', bic: 'GKCCBEBB', bank: 'Belfius Bank', code: [] },
  { country: 'BE', bic: 'CTBKBEBX', bank: 'CBC Banque', code: [] },
  { country: 'BE', bic: 'TRIOBEBB', bank: 'Triodos Bank Belgium', code: [] },
  { country: 'BE', bic: 'JVBABE22', bank: 'Bank J.Van Breda & C', code: [] },
  { country: 'BE', bic: 'NICABEBB', bank: 'Crelan', code: [] },
  { country: 'BE', bic: 'ARSPBE22', bank: 'Argenta Spaarbank', code: [] },
  {
    country: 'BG',
    bic: 'BPBIBGSF',
    bank: 'Postbank (Eurobank Bulgaria)',
    code: [],
  },
  { country: 'BG', bic: 'UNCRBGSF', bank: 'UniCredit Bulbank', code: [] },
  { country: 'BG', bic: 'SABORGSF', bank: 'DSK Bank', code: [] },
  { country: 'BG', bic: 'FINVBGSF', bank: 'First Investment Bank', code: [] },
  {
    country: 'BG',
    bic: 'CECBBGSF',
    bank: 'Central Cooperative Bank',
    code: [],
  },
  { country: 'BG', bic: 'STBGBGSF', bank: 'Municipal Bank', code: [] },
  {
    country: 'BG',
    bic: 'BGUSBGSF',
    bank: 'Bulgarian-American Credit Bank',
    code: [],
  },
  { country: 'BG', bic: 'RZBBGSF', bank: 'Raiffeisenbank Bulgaria', code: [] },
  {
    country: 'BG',
    bic: 'IABORBBGSF',
    bank: 'International Asset Bank',
    code: [],
  },
  { country: 'HR', bic: 'ZABAHR2X', bank: 'Zagrebacka banka d.d.', code: [] },
  {
    country: 'HR',
    bic: 'PBZGHR2X',
    bank: 'Privredna banka Zagreb d.d.',
    code: [],
  },
  {
    country: 'HR',
    bic: 'EABOR2X',
    bank: 'Erste & Steiermarkische Bank d.d.',
    code: [],
  },
  {
    country: 'HR',
    bic: 'RZBHHR2X',
    bank: 'Raiffeisenbank Austria d.d.',
    code: [],
  },
  { country: 'HR', bic: 'OTPVHR2X', bank: 'OTP banka d.d.', code: [] },
  { country: 'HR', bic: 'ADCRHR2X', bank: 'Addiko Bank d.d.', code: [] },
  {
    country: 'HR',
    bic: 'HABOHR22',
    bank: 'Hrvatska poštanska banka d.d.',
    code: [],
  },
  { country: 'CY', bic: 'BCYPCY2N', bank: 'Bank of Cyprus', code: [] },
  { country: 'CY', bic: 'HELOCY2N', bank: 'Hellenic Bank', code: [] },
  { country: 'CY', bic: 'ABKLCY2N', bank: 'Astrobank', code: [] },
  { country: 'CY', bic: 'RCBACY2N', bank: 'RCB Bank', code: [] },
  { country: 'CZ', bic: 'KOMBCZPP', bank: 'Komercni banka', code: [] },
  { country: 'CZ', bic: 'GIBACZPX', bank: 'CSOB', code: [] },
  { country: 'CZ', bic: 'CEKOCZPP', bank: 'Ceska sporitelna', code: [] },
  {
    country: 'CZ',
    bic: 'BACXCZPP',
    bank: 'UniCredit Bank Czech Republic',
    code: [],
  },
  { country: 'CZ', bic: 'FIOBCZPP', bank: 'Fio banka', code: [] },
  { country: 'CZ', bic: 'RZBCCZPP', bank: 'Raiffeisenbank a.s.', code: [] },
  { country: 'CZ', bic: 'AIRACZPP', bank: 'Air Bank', code: [] },
  { country: 'CZ', bic: 'BREXCZPP', bank: 'mBank Czech Republic', code: [] },
  { country: 'CZ', bic: 'AGBACZPP', bank: 'Moneta Money Bank', code: [] },
  { country: 'DK', bic: 'DABADKKK', bank: 'Danske Bank', code: [] },
  { country: 'DK', bic: 'NABORDEADKKK', bank: 'Nordea Danmark A/S', code: [] },
  { country: 'DK', bic: 'JYBADKKK', bank: 'Jyske Bank', code: [] },
  { country: 'DK', bic: 'SYBKDK22', bank: 'Sydbank', code: [] },
  { country: 'DK', bic: 'NYKBDKKK', bank: 'Nykredit Bank', code: [] },
  { country: 'DK', bic: 'SPNODK22', bank: 'Spar Nord Bank', code: [] },
  { country: 'DK', bic: 'RINGDK22', bank: 'Ringkjobing Landbobank', code: [] },
  { country: 'DK', bic: 'ALBADKKK', bank: 'Alm. Brand Bank', code: [] },
  { country: 'EE', bic: 'HABAEE2X', bank: 'Swedbank AS', code: [] },
  { country: 'EE', bic: 'EEUHEE2X', bank: 'SEB Pank', code: [] },
  { country: 'EE', bic: 'LHVBEE22', bank: 'LHV Pank', code: [] },
  { country: 'EE', bic: 'RIKOEE22', bank: 'Luminor Bank AS', code: [] },
  { country: 'EE', bic: 'EKRDEE22', bank: 'Inbank AS', code: [] },
  { country: 'EE', bic: 'COOPEE22', bank: 'Coop Pank AS', code: [] },
  { country: 'FI', bic: 'NDEAFIHH', bank: 'Nordea Bank Finland', code: [] },
  { country: 'FI', bic: 'OKOYFIHH', bank: 'OP Financial Group', code: [] },
  { country: 'FI', bic: 'DABAFIHH', bank: 'Danske Bank Finland', code: [] },
  { country: 'FI', bic: 'HANDFIHH', bank: 'Handelsbanken Finland', code: [] },
  { country: 'FI', bic: 'HELSFIHH', bank: 'Aktia Bank', code: [] },
  { country: 'FI', bic: 'AABORLANDFIHH', bank: 'Alandsbanken', code: [] },
  { country: 'FI', bic: 'POPFFI22', bank: 'POP Pankki', code: [] },
  { country: 'FI', bic: 'OMASFIHH', bank: 'Oma Savingsbank', code: [] },
  { country: 'FI', bic: 'SABORPANKFIHH', bank: 'S-Pankki', code: [] },
  { country: 'FR', bic: 'BNPAFRPP', bank: 'BNP Paribas', code: [] },
  { country: 'FR', bic: 'SOGEFRPP', bank: 'Societe Generale', code: [] },
  { country: 'FR', bic: 'CRLYFRPP', bank: 'Credit Lyonnais (LCL)', code: [] },
  { country: 'FR', bic: 'CEPAFRPP', bank: "Caisse d'Epargne", code: [] },
  { country: 'FR', bic: 'AGRIFRPP', bank: 'Credit Agricole', code: [] },
  { country: 'FR', bic: 'CMCIFRPP', bank: 'Credit Mutuel', code: [] },
  { country: 'FR', bic: 'CCBPFRPP', bank: 'Banque Populaire', code: [] },
  { country: 'FR', bic: 'BREDFRPP', bank: 'BRED Banque Populaire', code: [] },
  { country: 'FR', bic: 'BOUSFRPP', bank: 'Boursorama Banque', code: [] },
  { country: 'FR', bic: 'PSSTFRPP', bank: 'La Banque Postale', code: [] },
  { country: 'FR', bic: 'NATXFRPP', bank: 'Natixis', code: [] },
  { country: 'FR', bic: 'CMBRFRPP', bank: 'CIC', code: [] },
  { country: 'DE', bic: 'COBADEFF', bank: 'Commerzbank', code: [] },
  { country: 'DE', bic: 'DEUTDEFF', bank: 'Deutsche Bank', code: [] },
  {
    country: 'DE',
    bic: 'HYVEDEMM',
    bank: 'UniCredit Bank (HypoVereinsbank)',
    code: [],
  },
  { country: 'DE', bic: 'INGDDEFF', bank: 'ING-DiBa', code: [] },
  { country: 'DE', bic: 'PBNKDEFF', bank: 'Postbank', code: [] },
  { country: 'DE', bic: 'BYLADEM1', bank: 'Bayerische Landesbank', code: [] },
  {
    country: 'DE',
    bic: 'SOLADEST',
    bank: 'Landesbank Baden-Wurttemberg',
    code: [],
  },
  { country: 'DE', bic: 'HELADEFF', bank: 'Helaba Landesbank', code: [] },
  { country: 'DE', bic: 'GENODEFF', bank: 'DZ Bank', code: [] },
  { country: 'DE', bic: 'BELADEBE', bank: 'Berliner Sparkasse', code: [] },
  { country: 'DE', bic: 'NTSBDEB1', bank: 'N26 Bank GmbH', code: [] },
  { country: 'DE', bic: 'CODODEM1', bank: 'comdirect bank', code: [] },
  { country: 'DE', bic: 'TUBDDEDD', bank: 'Targobank', code: [] },
  { country: 'DE', bic: 'DAAEDEDD', bank: 'apoBank', code: [] },
  { country: 'DE', bic: 'CSDBDE71', bank: 'Consorsbank', code: [] },
  { country: 'GR', bic: 'ETHNGRAA', bank: 'National Bank of Greece', code: [] },
  { country: 'GR', bic: 'PIABORGRAA', bank: 'Piraeus Bank', code: [] },
  { country: 'GR', bic: 'EFGBGRAA', bank: 'Eurobank Ergasias', code: [] },
  { country: 'GR', bic: 'CRBAGRAA', bank: 'Alpha Bank', code: [] },
  { country: 'GR', bic: 'ATTIGRAA', bank: 'Attica Bank', code: [] },
  { country: 'GR', bic: 'OPGRGRAA', bank: 'Optima Bank', code: [] },
  { country: 'HU', bic: 'OTPVHUHB', bank: 'OTP Bank', code: [] },
  { country: 'HU', bic: 'GIBAHUHB', bank: 'Erste Bank Hungary', code: [] },
  { country: 'HU', bic: 'MABORHB2X', bank: 'K&H Bank', code: [] },
  { country: 'HU', bic: 'BACXHUHB', bank: 'UniCredit Bank Hungary', code: [] },
  {
    country: 'HU',
    bic: 'UABORBRTHUHB',
    bank: 'Raiffeisen Bank Hungary',
    code: [],
  },
  { country: 'HU', bic: 'CIBHHUHB', bank: 'CIB Bank', code: [] },
  { country: 'HU', bic: 'MKKBHUHB', bank: 'MKB Bank', code: [] },
  { country: 'HU', bic: 'TAKBHUHB', bank: 'Takarekbank', code: [] },
  { country: 'HU', bic: 'BUDAHUHB', bank: 'Budapest Bank', code: [] },
  { country: 'IS', bic: 'LABOISRE', bank: 'Landsbankinn', code: [] },
  { country: 'IS', bic: 'GLITISRE', bank: 'Islandsbanki', code: [] },
  { country: 'IS', bic: 'ARIONISS', bank: 'Arion Bank', code: [] },
  {
    country: 'IE',
    bic: 'AABORIBKIE2D',
    bank: 'AIB (Allied Irish Banks)',
    code: [],
  },
  { country: 'IE', bic: 'BOFIIE2D', bank: 'Bank of Ireland', code: [] },
  { country: 'IE', bic: 'PFBKIE22', bank: 'Permanent TSB', code: [] },
  {
    country: 'IE',
    bic: 'ABORIRULSIE2D',
    bank: 'Ulster Bank Ireland',
    code: [],
  },
  { country: 'IT', bic: 'BCITITMM', bank: 'Intesa Sanpaolo', code: [] },
  { country: 'IT', bic: 'UNCRITMM', bank: 'UniCredit', code: [] },
  {
    country: 'IT',
    bic: 'BNLIITRR',
    bank: 'Banca Nazionale del Lavoro (BNL)',
    code: [],
  },
  {
    country: 'IT',
    bic: 'PASCITMM',
    bank: 'Banca Monte dei Paschi di Siena',
    code: [],
  },
  { country: 'IT', bic: 'BAPOIT22', bank: 'Banco BPM', code: [] },
  { country: 'IT', bic: 'BPERIT3B', bank: 'BPER Banca', code: [] },
  { country: 'IT', bic: 'FIBKITMM', bank: 'FinecoBank', code: [] },
  { country: 'IT', bic: 'SELBIT2B', bank: 'Banca Sella', code: [] },
  { country: 'IT', bic: 'MEDBITM1', bank: 'Banca Mediolanum', code: [] },
  {
    country: 'IT',
    bic: 'POCAIT3A',
    bank: 'Poste Italiane (BancoPosta)',
    code: [],
  },
  { country: 'IT', bic: 'INGBITM1', bank: 'ING Italia', code: [] },
  {
    country: 'IT',
    bic: 'IABORCRRIITMM',
    bank: 'Credit Agricole Italia',
    code: [],
  },
  { country: 'LV', bic: 'HABALV22', bank: 'Swedbank Latvia', code: [] },
  { country: 'LV', bic: 'UNLALV2X', bank: 'SEB banka', code: [] },
  { country: 'LV', bic: 'RIKOLV2X', bank: 'Luminor Bank Latvia', code: [] },
  { country: 'LV', bic: 'PARXLV22', bank: 'Citadele banka', code: [] },
  { country: 'LV', bic: 'BLBALV21', bank: 'BluOr Bank', code: [] },
  {
    country: 'LV',
    bic: 'LATBLV22',
    bank: 'Baltic International Bank',
    code: [],
  },
  { country: 'LV', bic: 'RABORIETLV2X', bank: 'Rietumu Banka', code: [] },
  {
    country: 'LI',
    bic: 'BALPLI22',
    bank: 'Liechtensteinische Landesbank',
    code: [],
  },
  { country: 'LI', bic: 'VPBVLI2X', bank: 'VP Bank', code: [] },
  { country: 'LI', bic: 'LGTBLI22', bank: 'LGT Bank', code: [] },
  {
    country: 'LU',
    bic: 'BCLLLULL',
    bank: "Banque et Caisse d'Epargne de l'Etat (BCEE)",
    code: [],
  },
  { country: 'LU', bic: 'BGLLLULL', bank: 'BGL BNP Paribas', code: [] },
  {
    country: 'LU',
    bic: 'BILLLULL',
    bank: 'Banque Internationale a Luxembourg',
    code: [],
  },
  { country: 'LU', bic: 'CCRALULL', bank: 'Banque Raiffeisen', code: [] },
  { country: 'LU', bic: 'CELLLULL', bank: 'Banque de Luxembourg', code: [] },
  {
    country: 'LU',
    bic: 'DEUTLULL',
    bank: 'Deutsche Bank Luxembourg',
    code: [],
  },
  { country: 'MT', bic: 'VALLMTMT', bank: 'Bank of Valletta', code: [] },
  { country: 'MT', bic: 'HSBCMTMT', bank: 'HSBC Bank Malta', code: [] },
  { country: 'MT', bic: 'APABORMTMT', bank: 'APS Bank', code: [] },
  { country: 'MT', bic: 'MMEBMTMT', bank: 'MeDirect Bank', code: [] },
  { country: 'MT', bic: 'LOMPMT21', bank: 'Lombard Bank Malta', code: [] },
  { country: 'NL', bic: 'INGBNL2A', bank: 'ING Bank', code: [] },
  { country: 'NL', bic: 'RABONL2U', bank: 'Rabobank', code: [] },
  { country: 'NL', bic: 'ABNANL2A', bank: 'ABN AMRO Bank', code: [] },
  { country: 'NL', bic: 'SNSBNL2A', bank: 'de Volksbank (SNS)', code: [] },
  { country: 'NL', bic: 'TRIONL2U', bank: 'Triodos Bank', code: [] },
  { country: 'NL', bic: 'ASNBNL21', bank: 'ASN Bank', code: [] },
  { country: 'NL', bic: 'BUNQNL2A', bank: 'Bunq', code: [] },
  { country: 'NL', bic: 'KNABNL2H', bank: 'Knab', code: [] },
  { country: 'NO', bic: 'DNBANOKKK', bank: 'DNB Bank', code: [] },
  { country: 'NO', bic: 'NDEANOKK', bank: 'Nordea Norge', code: [] },
  { country: 'NO', bic: 'DABORNOKK', bank: 'Danske Bank Norge', code: [] },
  { country: 'NO', bic: 'HANDNOKK', bank: 'Handelsbanken Norge', code: [] },
  { country: 'NO', bic: 'SPABORNO22', bank: 'SpareBank 1 SR-Bank', code: [] },
  { country: 'NO', bic: 'SBAKNOBB', bank: 'SpareBank 1 Nord-Norge', code: [] },
  { country: 'NO', bic: 'SHEDNO22', bank: 'Sbanken', code: [] },
  { country: 'PL', bic: 'BPKOPLPW', bank: 'PKO Bank Polski', code: [] },
  { country: 'PL', bic: 'WBKPPLPP', bank: 'Santander Bank Polska', code: [] },
  { country: 'PL', bic: 'INGBPLPW', bank: 'ING Bank Slaski', code: [] },
  { country: 'PL', bic: 'BREXPLPW', bank: 'mBank', code: [] },
  { country: 'PL', bic: 'PKOPPLPW', bank: 'Bank Pekao', code: [] },
  { country: 'PL', bic: 'BIGBPLPW', bank: 'Bank Millennium', code: [] },
  { country: 'PL', bic: 'CITIPLPX', bank: 'Citi Handlowy', code: [] },
  { country: 'PL', bic: 'ALBPPLPW', bank: 'Alior Bank', code: [] },
  { country: 'PL', bic: 'PPABPLPK', bank: 'BNP Paribas Bank Polska', code: [] },
  { country: 'PL', bic: 'GBGCPLPK', bank: 'Getin Noble Bank', code: [] },
  {
    country: 'PL',
    bic: 'RCABORPLPW',
    bank: 'Credit Agricole Bank Polska',
    code: [],
  },
  {
    country: 'PT',
    bic: 'CGDIPTPL',
    bank: 'Caixa Geral de Depositos',
    code: [],
  },
  { country: 'PT', bic: 'BCOMPTPL', bank: 'Millennium BCP', code: [] },
  { country: 'PT', bic: 'BESCPTPL', bank: 'Novo Banco', code: [] },
  { country: 'PT', bic: 'TOTTPTPL', bank: 'Santander Totta', code: [] },
  { country: 'PT', bic: 'BBPIPTPL', bank: 'Banco BPI', code: [] },
  { country: 'PT', bic: 'CCCMPTPL', bank: 'Banco Montepio', code: [] },
  { country: 'PT', bic: 'BAPIPTPL', bank: 'ActivoBank', code: [] },
  { country: 'PT', bic: 'CCAMPTPL', bank: 'Credito Agricola', code: [] },
  { country: 'RO', bic: 'BTRLRO22', bank: 'Banca Transilvania', code: [] },
  {
    country: 'RO',
    bic: 'BRDEROBU',
    bank: 'BRD - Groupe Societe Generale',
    code: [],
  },
  { country: 'RO', bic: 'RZBRROBU', bank: 'Raiffeisen Bank Romania', code: [] },
  { country: 'RO', bic: 'BACXROBU', bank: 'UniCredit Bank Romania', code: [] },
  { country: 'RO', bic: 'INGBROBU', bank: 'ING Bank Romania', code: [] },
  { country: 'RO', bic: 'BUCRROBU', bank: 'Alpha Bank Romania', code: [] },
  { country: 'RO', bic: 'CECEROBU', bank: 'CEC Bank', code: [] },
  { country: 'RO', bic: 'OTPVROBU', bank: 'OTP Bank Romania', code: [] },
  {
    country: 'RO',
    bic: 'BCRNROBU',
    bank: 'BCR (Erste Group Romania)',
    code: [],
  },
  { country: 'SK', bic: 'TATRSKBX', bank: 'Tatra banka', code: [] },
  { country: 'SK', bic: 'SUBASKBX', bank: 'VUB banka', code: [] },
  { country: 'SK', bic: 'GIBASKBX', bank: 'Slovenska sporitelna', code: [] },
  { country: 'SK', bic: 'CEKOSKBX', bank: 'CSOB Slovensko', code: [] },
  { country: 'SK', bic: 'UNCRSKBX', bank: 'UniCredit Bank Slovakia', code: [] },
  { country: 'SK', bic: 'POBNSKBA', bank: 'Postova banka', code: [] },
  { country: 'SK', bic: 'FIOZSKBA', bank: 'Fio banka Slovensko', code: [] },
  { country: 'SI', bic: 'LJBASI2X', bank: 'NLB d.d.', code: [] },
  { country: 'SI', bic: 'KSPOSI22', bank: 'Nova KBM d.d.', code: [] },
  { country: 'SI', bic: 'SKBASI2X', bank: 'SKB banka d.d.', code: [] },
  { country: 'SI', bic: 'ABOSI2X', bank: 'Abanka d.d.', code: [] },
  {
    country: 'SI',
    bic: 'ADBASI2X',
    bank: 'Addiko Bank d.d. Slovenia',
    code: [],
  },
  {
    country: 'SI',
    bic: 'INTRSISE',
    bank: 'Intesa Sanpaolo Bank d.d.',
    code: [],
  },
  { country: 'SI', bic: 'GORESI2X', bank: 'Gorenjska banka', code: [] },
  { country: 'ES', bic: 'CAIXESBB', bank: 'CaixaBank', code: [] },
  { country: 'ES', bic: 'BBVAESMM', bank: 'BBVA', code: [] },
  { country: 'ES', bic: 'BSCHESMM', bank: 'Banco Santander', code: [] },
  { country: 'ES', bic: 'BSABESMM', bank: 'Banco Sabadell', code: [] },
  { country: 'ES', bic: 'BKBKESMM', bank: 'Bankinter', code: [] },
  { country: 'ES', bic: 'UCJAES2M', bank: 'Unicaja Banco', code: [] },
  { country: 'ES', bic: 'CAABORGLESMM', bank: 'Abanca', code: [] },
  { country: 'ES', bic: 'BASKES2B', bank: 'Kutxabank', code: [] },
  { country: 'ES', bic: 'CAZRES2Z', bank: 'Ibercaja Banco', code: [] },
  { country: 'ES', bic: 'INGDESMM', bank: 'ING Espana', code: [] },
  { country: 'ES', bic: 'OPENESMM', bank: 'Openbank', code: [] },
  { country: 'ES', bic: 'EVOBESMM', bank: 'EVO Banco', code: [] },
  { country: 'SE', bic: 'NDEASESS', bank: 'Nordea Sweden', code: [] },
  { country: 'SE', bic: 'HANDSESS', bank: 'Handelsbanken', code: [] },
  { country: 'SE', bic: 'SWEDSESS', bank: 'Swedbank', code: [] },
  { country: 'SE', bic: 'ESSESESS', bank: 'SEB', code: [] },
  { country: 'SE', bic: 'DABASESX', bank: 'Danske Bank Sweden', code: [] },
  { country: 'SE', bic: 'SKIASESS', bank: 'Skandiabanken', code: [] },
  { country: 'SE', bic: 'ELLFSESS', bank: 'Collector Bank', code: [] },
  { country: 'SE', bic: 'AABORVANSESS', bank: 'Avanza Bank', code: [] },
  { country: 'SE', bic: 'LANSSESS', bank: 'Lansforsakringar Bank', code: [] },
];
